import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';

// Inline types mirroring shared-types (shared-types uses .js ESM imports
// incompatible with CRA TS 4.9 + moduleResolution:node)
type Discipline =
  | 'SINGLES_M'
  | 'SINGLES_F'
  | 'DOUBLES_M'
  | 'DOUBLES_F'
  | 'MIXED'
  | 'CUSTOM';

const DOUBLES_DISCIPLINES: Discipline[] = ['DOUBLES_M', 'DOUBLES_F', 'MIXED'];

// CUSTOM discipline defaults to singles UX — admin defines what it means
function isDoubles(discipline: Discipline): boolean {
  return DOUBLES_DISCIPLINES.includes(discipline);
}

const DISCIPLINE_LABELS: Record<Discipline, string> = {
  SINGLES_M: 'Singiel mężczyzn',
  SINGLES_F: 'Singiel kobiet',
  DOUBLES_M: 'Debel mężczyzn',
  DOUBLES_F: 'Debel kobiet',
  MIXED: 'Mikst',
  CUSTOM: 'Własna',
};

interface PlayerSuggestion {
  id: number;
  firstName: string;
  lastName: string;
}

interface NewPlayerFields {
  firstName: string;
  lastName: string;
  gender: 'MALE' | 'FEMALE' | '';
  birthDate: string;
  club: string;
  email: string;
  phone: string;
}

const emptyNewPlayer = (): NewPlayerFields => ({
  firstName: '',
  lastName: '',
  gender: '',
  birthDate: '',
  club: '',
  email: '',
  phone: '',
});

interface PlayerPickerProps {
  label: string;
  onSelect: (ref: PlayerRef) => void;
}

type PlayerRef =
  | { playerId: number }
  | { newPlayer: NewPlayerFields };

function PlayerPicker({ label, onSelect }: PlayerPickerProps) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<PlayerSuggestion[]>([]);
  const [selected, setSelected] = useState<PlayerSuggestion | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [newPlayer, setNewPlayer] = useState<NewPlayerFields>(emptyNewPlayer());
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchSuggestions = useCallback((q: string) => {
    if (q.length < 2) {
      setSuggestions([]);
      return;
    }

    fetch(`/api/players?search=${encodeURIComponent(q)}`)
      .then((res) => res.json())
      .then((data: PlayerSuggestion[]) => setSuggestions(data))
      .catch(() => setSuggestions([]));
  }, []);

  const handleQueryChange = (value: string) => {
    setQuery(value);
    setSelected(null);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => fetchSuggestions(value), 300);
  };

  const handleSelect = (player: PlayerSuggestion) => {
    setSelected(player);
    setQuery(`${player.firstName} ${player.lastName}`);
    setSuggestions([]);
    setShowNew(false);
    onSelect({ playerId: player.id });
  };

  const handleNewPlayerChange = (field: keyof NewPlayerFields, value: string) => {
    const updated = { ...newPlayer, [field]: value };
    setNewPlayer(updated);
    onSelect({ newPlayer: updated });
  };

  const toggleNew = () => {
    const next = !showNew;
    setShowNew(next);
    if (next) {
      setSelected(null);
      setQuery('');
      setSuggestions([]);
      onSelect({ newPlayer: newPlayer });
    }
  };

  return (
    <div className="player-picker">
      <label className="player-picker-label">{label}</label>
      {!showNew && (
        <div className="player-picker-search">
          <input
            type="text"
            placeholder="Szukaj zawodnika..."
            value={query}
            onChange={(e) => handleQueryChange(e.target.value)}
            className="player-picker-input"
          />
          {selected && (
            <div className="player-picker-selected">
              Wybrano: <strong>{selected.firstName} {selected.lastName}</strong>
            </div>
          )}
          {suggestions.length > 0 && !selected && (
            <ul className="player-picker-suggestions">
              {suggestions.map((p) => (
                <li
                  key={p.id}
                  onClick={() => handleSelect(p)}
                  className="player-picker-suggestion"
                >
                  {p.firstName} {p.lastName}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <button type="button" onClick={toggleNew} className="player-picker-toggle">
        {showNew ? 'Wróć do wyszukiwania' : 'Nowy zawodnik'}
      </button>
      {showNew && (
        <div className="new-player-form">
          <input
            placeholder="Imię *"
            value={newPlayer.firstName}
            onChange={(e) => handleNewPlayerChange('firstName', e.target.value)}
            required
          />
          <input
            placeholder="Nazwisko *"
            value={newPlayer.lastName}
            onChange={(e) => handleNewPlayerChange('lastName', e.target.value)}
            required
          />
          <select
            value={newPlayer.gender}
            onChange={(e) => handleNewPlayerChange('gender', e.target.value)}
            required
          >
            <option value="">Płeć *</option>
            <option value="MALE">Mężczyzna</option>
            <option value="FEMALE">Kobieta</option>
          </select>
          <input
            type="date"
            placeholder="Data urodzenia"
            value={newPlayer.birthDate}
            onChange={(e) => handleNewPlayerChange('birthDate', e.target.value)}
          />
          <input
            placeholder="Klub"
            value={newPlayer.club}
            onChange={(e) => handleNewPlayerChange('club', e.target.value)}
          />
          <input
            type="email"
            placeholder="E-mail"
            value={newPlayer.email}
            onChange={(e) => handleNewPlayerChange('email', e.target.value)}
          />
          <input
            type="tel"
            placeholder="Telefon"
            value={newPlayer.phone}
            onChange={(e) => handleNewPlayerChange('phone', e.target.value)}
          />
        </div>
      )}
    </div>
  );
}

function buildPlayerPayload(ref: PlayerRef | null): object | null {
  if (!ref) {
    return null;
  }

  if ('playerId' in ref) {
    return { playerId: ref.playerId };
  }

  const np = ref.newPlayer;
  if (!np.firstName.trim() || !np.lastName.trim() || !np.gender) {
    return null;
  }

  const payload: Record<string, unknown> = {
    firstName: np.firstName.trim(),
    lastName: np.lastName.trim(),
    gender: np.gender,
  };

  if (np.birthDate) {
    payload.birthDate = np.birthDate;
  }

  if (np.club.trim()) {
    payload.club = np.club.trim();
  }

  if (np.email.trim()) {
    payload.email = np.email.trim();
  }

  if (np.phone.trim()) {
    payload.phone = np.phone.trim();
  }

  return { newPlayer: payload };
}

interface CategoryMeta {
  id: number;
  name: string;
  discipline: Discipline;
}

const RegistrationForm: React.FC = () => {
  const { id: tournamentId, categoryId } = useParams<{ id: string; categoryId: string }>();
  const [category, setCategory] = useState<CategoryMeta | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [p1Ref, setP1Ref] = useState<PlayerRef | null>(null);
  const [p2Ref, setP2Ref] = useState<PlayerRef | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Fetch category discipline from event detail
  useEffect(() => {
    if (!tournamentId) {
      return;
    }

    fetch(`/api/public/events/${tournamentId}`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        return res.json();
      })
      .then((data: { categories: CategoryMeta[] }) => {
        const cat = data.categories.find((c) => c.id === Number(categoryId));
        if (!cat) {
          setLoadError('Nie znaleziono kategorii.');
        } else {
          setCategory(cat);
        }
      })
      .catch(() => setLoadError('Nie udało się załadować kategorii.'));
  }, [tournamentId, categoryId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!category || !categoryId) {
      return;
    }

    setSubmitError(null);
    const doubles = isDoubles(category.discipline);

    let body: object;

    if (doubles) {
      const p1Payload = buildPlayerPayload(p1Ref);
      const p2Payload = buildPlayerPayload(p2Ref);
      if (!p1Payload || !p2Payload) {
        setSubmitError('Uzupełnij dane obu zawodników.');
        return;
      }

      body = { p1: p1Payload, p2: p2Payload };
    } else {
      const p1Payload = buildPlayerPayload(p1Ref);
      if (!p1Payload) {
        setSubmitError('Uzupełnij dane zawodnika.');
        return;
      }

      body = p1Payload;
    }

    setSubmitting(true);

    try {
      const res = await fetch(`/api/categories/${categoryId}/registrations/public`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (res.status === 201) {
        setSuccess(true);
      } else if (res.status === 409) {
        setSubmitError('Ten zawodnik jest już zapisany w tej kategorii.');
      } else if (res.status === 422) {
        const data = await res.json().catch(() => ({}));
        setSubmitError(`Błąd walidacji: ${data.error ?? 'Nieprawidłowe dane.'}`);
      } else if (res.status === 429) {
        setSubmitError('Za dużo prób. Spróbuj ponownie za chwilę.');
      } else {
        setSubmitError('Nieoczekiwany błąd. Spróbuj ponownie.');
      }
    } catch {
      setSubmitError('Błąd sieci. Spróbuj ponownie.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError) {
    return <div className="reg-form-error">{loadError}</div>;
  }

  if (!category) {
    return <div className="reg-form-loading">Ładowanie...</div>;
  }

  if (success) {
    return (
      <div className="reg-form-success">
        <h2>Rejestracja zakończona!</h2>
        <p>Zostałeś/-aś pomyślnie zapisany/-a.</p>
        <Link to={`/events/${tournamentId}`}>Wróć do turnieju</Link>
      </div>
    );
  }

  const doubles = isDoubles(category.discipline);

  return (
    <div className="reg-form">
      <Link to={`/events/${tournamentId}`} className="reg-form-back">
        ← Powrót
      </Link>
      <h1>Zapisz się</h1>
      <h2 className="reg-form-category">{category.name}</h2>
      <div className="reg-form-discipline">
        {DISCIPLINE_LABELS[category.discipline]}
        {doubles && <span className="reg-form-doubles-badge"> (debel)</span>}
      </div>
      <form onSubmit={handleSubmit} noValidate>
        <PlayerPicker
          label={doubles ? 'Zawodnik 1' : 'Zawodnik'}
          onSelect={setP1Ref}
        />
        {doubles && (
          <PlayerPicker label="Zawodnik 2" onSelect={setP2Ref} />
        )}
        {submitError && <div className="reg-form-submit-error">{submitError}</div>}
        <button type="submit" disabled={submitting} className="reg-form-submit">
          {submitting ? 'Wysyłanie...' : 'Zapisz się'}
        </button>
      </form>
    </div>
  );
};

export default RegistrationForm;
